/**
 * Image validation and resizing utilities for Apple Wallet pass generation
 * Handles dimension validation and automatic generation of multiple resolutions
 */

export interface ImageSpec {
  name: string;
  width: number;
  height: number;
  description: string;
}

export interface ImageResolutions {
  '1x': ImageSpec;
  '2x': ImageSpec;
  '3x': ImageSpec;
}

export interface ResizedImages {
  '1x': File;
  '2x': File;
  '3x': File;
}

// Apple Wallet pass image specifications
export const LOGO_SPECS: ImageResolutions = {
  '1x': { name: 'logo', width: 160, height: 50, description: 'Logo 1x' },
  '2x': { name: 'logo@2x', width: 320, height: 100, description: 'Logo 2x' },
  '3x': { name: 'logo@3x', width: 480, height: 150, description: 'Logo 3x' }
};

export const LOGO_SQUARE_SPECS: ImageResolutions = {
  '1x': { name: 'logo', width: 50, height: 50, description: 'Square Logo 1x' },
  '2x': { name: 'logo@2x', width: 100, height: 100, description: 'Square Logo 2x' },
  '3x': { name: 'logo@3x', width: 150, height: 150, description: 'Square Logo 3x' }
};

export const LOGO_GOOGLE_SQUARE_SPECS: ImageResolutions = {
  '1x': { name: 'logo', width: 660, height: 660, description: 'Google Square Logo' },
  '2x': { name: 'logo', width: 660, height: 660, description: 'Google Square Logo' },
  '3x': { name: 'logo', width: 660, height: 660, description: 'Google Square Logo' }
};

export const STRIP_SPECS: ImageResolutions = {
  '1x': { name: 'strip', width: 375, height: 144, description: 'Strip 1x' },
  '2x': { name: 'strip@2x', width: 750, height: 288, description: 'Strip 2x' },
  '3x': { name: 'strip@3x', width: 1125, height: 432, description: 'Strip 3x' }
};

export type ImageType = 'logo' | 'logo_square' | 'logo_google_square' | 'strip';

/**
 * Get the specifications for a given image type
 */
export function getImageSpecs(imageType: ImageType): ImageResolutions {
  if (imageType === 'logo') return LOGO_SPECS;
  if (imageType === 'logo_square') return LOGO_SQUARE_SPECS;
  if (imageType === 'logo_google_square') return LOGO_GOOGLE_SQUARE_SPECS;
  return STRIP_SPECS;
}

/**
 * Validates if a file is a PNG image
 * @param file The file to validate
 * @returns Object with validation result and message
 */
export function validatePNGFormat(file: File): {
  valid: boolean;
  message: string;
} {
  const isPNG = file.type === 'image/png';

  return {
    valid: isPNG,
    message: isPNG
      ? 'File format is correct (PNG)'
      : `File must be in PNG format. Your file is ${file.type || 'unknown format'}.`
  };
}

/**
 * Validates if an image matches the exact required dimensions for @3x resolution
 * @param file The image file to validate
 * @param imageType The type of image (logo or strip)
 * @returns Promise with validation result and actual dimensions
 */
export async function validateImageDimensions(
  file: File,
  imageType: ImageType
): Promise<{
  valid: boolean;
  actualWidth: number;
  actualHeight: number;
  expectedWidth: number;
  expectedHeight: number;
  message: string;
}> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      const specs = getImageSpecs(imageType);
      const expected = specs['3x'];
      const actualWidth = img.naturalWidth;
      const actualHeight = img.naturalHeight;

      const valid = actualWidth === expected.width && actualHeight === expected.height;

      const message = valid
        ? `Image dimensions are correct: ${actualWidth} × ${actualHeight} pixels`
        : `Image must be exactly ${expected.width} × ${expected.height} pixels. Your image is ${actualWidth} × ${actualHeight} pixels.`;

      resolve({
        valid,
        actualWidth,
        actualHeight,
        expectedWidth: expected.width,
        expectedHeight: expected.height,
        message
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({
        valid: false,
        actualWidth: 0,
        actualHeight: 0,
        expectedWidth: getImageSpecs(imageType)['3x'].width,
        expectedHeight: getImageSpecs(imageType)['3x'].height,
        message: 'Failed to load image. Please ensure the file is a valid image.'
      });
    };

    img.src = url;
  });
}

/**
 * Resizes an image to a specific width and height using canvas
 * @param file Original image file
 * @param targetWidth Target width in pixels
 * @param targetHeight Target height in pixels
 * @param fileName Name for the resized file
 * @returns Promise with the resized image as a File
 */
async function resizeImage(
  file: File,
  targetWidth: number,
  targetHeight: number,
  fileName: string
): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      // Create canvas with target dimensions
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Use high-quality image smoothing
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw the resized image
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      // Convert canvas to blob and then to File
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to create blob from canvas'));
            return;
          }

          const resizedFile = new File([blob], fileName, {
            type: 'image/png',
            lastModified: Date.now()
          });

          resolve(resizedFile);
        },
        'image/png',
        1.0 // Maximum quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for resizing'));
    };

    img.src = url;
  });
}

/**
 * Generates all three resolutions (@3x, @2x, @1x) from a @3x source image
 * @param file The @3x resolution image file
 * @param imageType The type of image (logo or strip)
 * @returns Promise with all three resolution files
 */
export async function generateMultipleResolutions(
  file: File,
  imageType: ImageType
): Promise<ResizedImages> {
  const specs = getImageSpecs(imageType);
  const baseName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension

  try {
    // Generate @2x and @1x versions (keep original @3x)
    const [image2x, image1x] = await Promise.all([
      resizeImage(file, specs['2x'].width, specs['2x'].height, `${baseName}@2x.png`),
      resizeImage(file, specs['1x'].width, specs['1x'].height, `${baseName}@1x.png`)
    ]);

    // Return all three resolutions
    return {
      '3x': file, // Original file is already @3x
      '2x': image2x,
      '1x': image1x
    };
  } catch (error) {
    throw new Error(`Failed to generate multiple resolutions: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Helper to get display text for recommended dimensions
 */
export function getRecommendedDimensionsText(imageType: ImageType): string {
  const specs = getImageSpecs(imageType);
  return `${specs['3x'].width} × ${specs['3x'].height} pixels`;
}

/**
 * Helper to format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}
